/**
 * Backups Page
 *
 * Superuser-only encrypted backup & restore. The passphrase never leaves this
 * form except as a request header; it is required to both create and later
 * decrypt an archive, and a backup cannot be recovered without it.
 */

import React, { useRef, useState } from 'react';
import { Title, useNotify, useTranslate } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CloudDownloadRounded as DownloadIcon,
  RestoreRounded as RestoreIcon,
} from '@mui/icons-material';
import { API_CONFIG, USER_ROLES } from '@/config/constants';
import { getSsoToken } from '@/auth/sso';
import { hasRole } from '@/utils/auth';

const PASSPHRASE_HEADER = 'X-Backup-Passphrase';
const MIN_PASSPHRASE = 8;

const authHeaders = async (): Promise<Record<string, string>> => {
  const token = await getSsoToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const BackupsPage: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();

  const [backupPass, setBackupPass] = useState('');
  const [backupPass2, setBackupPass2] = useState('');
  const [downloading, setDownloading] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const [restorePass, setRestorePass] = useState('');
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);

  if (!hasRole(USER_ROLES.SUPERUSER)) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={translate('backups.title')} />
        <Alert severity="warning">{translate('backups.forbidden')}</Alert>
      </Box>
    );
  }

  const backupValid = backupPass.length >= MIN_PASSPHRASE && backupPass === backupPass2;
  const restoreValid = restoreFile != null && restorePass.length >= MIN_PASSPHRASE;

  const handleBackup = async (): Promise<void> => {
    setDownloading(true);
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/backups/download`, {
        headers: { ...(await authHeaders()), [PASSPHRASE_HEADER]: backupPass },
      });
      if (!response.ok) {
        throw new Error(await response.text().catch(() => ''));
      }

      const disposition = response.headers.get('Content-Disposition') ?? '';
      const match = /filename="?([^"]+)"?/.exec(disposition);
      const name = match?.[1] ?? 'contacts-backup.rmtkbk';

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      notify('backups.backupStarted', { type: 'info' });
    } catch {
      notify('backups.backupFailed', { type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const handleRestore = async (): Promise<void> => {
    setConfirmOpen(false);
    if (!restoreFile) {
      return;
    }
    setRestoring(true);
    try {
      const body = new FormData();
      body.append('file', restoreFile);
      const response = await fetch(`${API_CONFIG.BASE_URL}/backups/restore`, {
        method: 'POST',
        headers: { ...(await authHeaders()), [PASSPHRASE_HEADER]: restorePass },
        body,
      });
      if (!response.ok) {
        throw new Error(await response.text().catch(() => ''));
      }
      notify('backups.restoreOk', { type: 'success' });
      setRestoreFile(null);
      setRestorePass('');
      if (fileRef.current) {
        fileRef.current.value = '';
      }
    } catch {
      notify('backups.restoreFailed', { type: 'error' });
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Title title={translate('backups.title')} />

      <Card sx={{ mt: 1, maxWidth: 720 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {translate('backups.backupTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate('backups.backupHint')}
          </Typography>
          <Stack spacing={2} sx={{ maxWidth: 420 }}>
            <TextField
              type="password"
              label={translate('backups.passphrase')}
              value={backupPass}
              onChange={(e) => setBackupPass(e.target.value)}
              helperText={translate('backups.passphraseHint', { min: MIN_PASSPHRASE })}
              autoComplete="new-password"
            />
            <TextField
              type="password"
              label={translate('backups.passphraseConfirm')}
              value={backupPass2}
              onChange={(e) => setBackupPass2(e.target.value)}
              error={backupPass2.length > 0 && backupPass !== backupPass2}
              autoComplete="new-password"
            />
            <Box>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                disabled={!backupValid || downloading}
                onClick={handleBackup}
              >
                {translate('backups.download')}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3, maxWidth: 720 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {translate('backups.restoreTitle')}
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate('backups.restoreWarning')}
          </Alert>
          <Stack spacing={2} sx={{ maxWidth: 420 }}>
            <input
              ref={fileRef}
              type="file"
              accept=".rmtkbk"
              onChange={(e) => setRestoreFile(e.target.files?.[0] ?? null)}
            />
            <TextField
              type="password"
              label={translate('backups.passphrase')}
              value={restorePass}
              onChange={(e) => setRestorePass(e.target.value)}
              autoComplete="off"
            />
            <Box>
              <Button
                variant="contained"
                color="warning"
                startIcon={<RestoreIcon />}
                disabled={!restoreValid || restoring}
                onClick={() => setConfirmOpen(true)}
              >
                {translate('backups.restore')}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{translate('backups.confirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{translate('backups.confirmBody')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{translate('ra.action.cancel')}</Button>
          <Button color="warning" variant="contained" onClick={handleRestore}>
            {translate('backups.restore')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BackupsPage;
